import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import {
  IconArrowUp,
  IconCalendarOutline,
  IconStarOutline,
} from '@iwoca/icons';
import { Trans } from '@lingui/react/macro';
import Rive from '@rive-app/react-canvas';

import styles from './CheckEligibilityDE.module.css';
// @ts-expect-error does not recognise riv import.
import successTick from '../../../assets/animation/successTick.riv';
import { calculateEligibleAmount } from '../../../helpers/calculateEligibleAmount';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useNumberFormat } from '../../../hooks/useNumberFormat';
import { useSignupRouterDE } from '../../../hooks/useSignupRouter/useSignupRouterDE';
import {
  useGetApplicationQuery,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../lendingApi/lendingApiQueries';
import { TGetStateResponse } from '../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { normaliseStepDataDE } from '../../helpers/stateNormalisation';
import { CHECK_ELIGIBILITY_STEP_ID } from '../../helpers/steps';

const CheckEligibilityDE = () => {
  const [isNavigating, setIsNavigating] = useState(false);

  const { formatCurrency } = useNumberFormat();
  const { navigateSignupDE } = useSignupRouterDE();

  const { isStateFetching } = useGetStateQuery<TGetStateResponse>();
  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepDataDE(data)[CHECK_ELIGIBILITY_STEP_ID],
  );
  const stateKey = useContextStateKey();

  const { application } = useGetApplicationQuery();

  const requestedAmount = application?.requested_products?.[0].amount;

  const eligibleAmount = calculateEligibleAmount({
    annualTurnover: normalisedState.last12MonthsTurnover || 0,
    requestedAmount,
  });

  const formattedEligibleAmount = formatCurrency(eligibleAmount);

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Eligibility check',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  const navigateToTradingHistory = async () => {
    setIsNavigating(true);

    await navigateSignupDE('schritt-3', stateKey);

    setIsNavigating(false);
  };

  return (
    <section className={styles.checkEligibilityContainer}>
      <div className={styles.animationContainer}>
        <Rive
          src={successTick}
          className={styles.accountSuccessTickRive}
          data-testid="success-tick-animation"
        />
      </div>
      <div className={styles.titleContainer}>
        <h1 className={styles.title}>
          <Trans>Good news, you’re eligible!</Trans>
        </h1>
        <h1>Sie erfüllen unsere Anforderungen*</h1>
      </div>

      <section className={styles.benefitsCard}>
        <h3 className={styles.eligibleAmountText}>
          <Trans>Your business could borrow up to</Trans>{' '}
          {formattedEligibleAmount}
        </h3>

        <div className={styles.benefitsContainer}>
          <div className={styles.benefit}>
            <IconStarOutline className={styles.benefitIcon} />
            <p>
              <Trans>Repay early for free at any time</Trans>
            </p>
          </div>
          <div className={styles.benefit}>
            <IconCalendarOutline className={styles.benefitIcon} />
            <p>
              <Trans>Borrow from 1 day to 60 months</Trans>
            </p>
          </div>
          <div className={styles.benefit}>
            <IconArrowUp className={styles.benefitIcon} />
            <p>
              <Trans>Increases in credit possible</Trans>
            </p>
          </div>
        </div>
      </section>
      <p className={styles.terms}>
        *
        <Trans>
          To confirm your information, we will need easily accessible documents
          in the next step.
        </Trans>
      </p>

      <Button
        className={styles.nextButton}
        disabled={isStateFetching}
        loading={isNavigating}
        type="submit"
        onClick={navigateToTradingHistory}
      >
        <Trans>Upload Documents</Trans>
      </Button>
    </section>
  );
};

export default CheckEligibilityDE;
